import React, {Fragment, memo} from 'react';
import dynamic from 'next/dynamic';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import type {SxProps, Theme} from '@mui/material/styles';
import {colors, fontFamily, fontSize, fontWeight, spacing} from '@/styles/tokens';
const DisplayImage = dynamic(import('@/components/Ui-components/display-image'));

// Preserved from ChainProcess.module.scss — scoped to this component's own
// Box/Typography calls, no shared-component API change needed.
const stepsContentWrapperSx = {marginTop: '10px'} as const;

const stepsHeadingSx = {
    fontFamily: `${fontFamily.heading} !important`,
    fontWeight: `${fontWeight.bold} !important`,
    fontSize: `${fontSize[18]} !important`,
    color: `${colors.primary.main} !important`
} as const;

const processContentSx = {
    fontFamily: `${fontFamily.body} !important`,
    fontSize: `${fontSize[16]} !important`,
    fontWeight: `${fontWeight.regular} !important`,
    color: colors.text.primary,
    marginTop: '5px'
} as const;

const stepsImageWrapperSx = {
    background: '#fff',
    width: '100px',
    margin: '0 auto',
    height: '60px'
} as const;

const stepsWrapperCommonSx = {
    width: 'auto',
    margin: '30px auto',
    '@media screen and (max-width: 600px)': {
        width: 'auto',
        margin: '10px auto'
    }
} as const;

const stepsWrapperMaintenanceSx = {
    marginTop: '30px',
    left: '3%',
    textAlign: 'center',
    '&::before': {
        '@media screen and (max-width: 600px)': {
            content: "''",
            position: 'absolute !important',
            border: `1px solid ${colors.accent.orange} !important`,
            width: '2px',
            height: '50px',
            top: '-65px'
        }
    },
    '@media screen and (max-width: 600px)': {
        marginTop: `${spacing[60]} !important`,
        position: 'relative !important'
    },
    '&:first-child::before': {
        '@media screen and (max-width: 600px)': {
            border: 'none'
        }
    }
} as const;

const stepsWrapperMaintenance1Sx = {
    '&::before': {
        '@media screen and (max-width: 600px)': {
            border: 'none !important'
        }
    },
    '@media (max-width: 430px)': {
        marginTop: '10px !important'
    },
    '@media (max-width: 325px)': {
        marginTop: '10px !important'
    }
} as const;

const outerBorderBoxSx = {
    position: 'relative',
    '&::before': {
        content: "''",
        position: 'absolute',
        border: `2px solid ${colors.accent.orange}`,
        width: '84%',
        top: '75px',
        bottom: 0,
        borderRadius: '0 50px 50px 0px',
        height: '175px',
        borderLeft: 'none',
        zIndex: -1,
        right: 0,
        '@media screen and (max-width: 600px)': {
            display: 'none'
        },
        '@media (max-width: 430px)': {
            content: "''",
            position: 'absolute',
            border: 'none !important',
            top: 0,
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            borderLeft: `2px solid ${colors.accent.orange} !important`,
            zIndex: -1
        }
    },
    '&:last-child::after': {
        content: "''",
        border: 'none !important'
    }
} as const;

const Process = (props: any) => {
    const {data, heading, subheading, wrapperSx} = props;
    return (
        <Container maxWidth="xl">
            <Box className="spectrum-header-wrapper solutions">
                <Typography className="spectrum-heading color-black" component="h3">
                    {heading}
                </Typography>
                <Box className="divder-wrapper">
                    <hr className="divider-line" />
                </Box>
                <Box className="sub-heading-box text-padding">
                    <Typography className="spectrum-sub-heading color-black">{subheading}</Typography>
                </Box>
            </Box>
            <Box sx={[outerBorderBoxSx, {flexGrow: 1}, wrapperSx] as SxProps<Theme>}>
                <Grid container spacing={2} justifyContent="flex-end">
                    {data?.map((item: any, index: number) => (
                        <Fragment key={index}>
                            <Grid item xs={12} sm={4} md={4}>
                                <Box
                                    sx={{
                                        ...stepsWrapperMaintenanceSx,
                                        ...stepsWrapperCommonSx,
                                        ...(index === 0 && stepsWrapperMaintenance1Sx)
                                    }}
                                    className="step-idea-first"
                                >
                                    <Box sx={stepsImageWrapperSx}>
                                        <DisplayImage src={item.icon} alt={item.alt} width={60} height={60} />
                                    </Box>
                                    <Box sx={stepsContentWrapperSx}>
                                        <Typography component="p" sx={stepsHeadingSx}>
                                            {item.label}
                                        </Typography>

                                        <Typography component="p" sx={processContentSx}>
                                            {item.content}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        </Fragment>
                    ))}
                </Grid>
            </Box>
        </Container>
    );
};

export default memo(Process);
